
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    {
      /* KEYWORDS: apache cordova mobile hybrid framework guide tutorial */
    }
    <h1>{`Getting Started with Apache Cordova`}</h1>
    <h2>{`Setting up your environment`}</h2>
    <p>{`You'll need to start by following the general instructions for setting up your development environment for mobile found in our `}<a parentName="p" {...{
        "href": "/development/environment"
      }}>{`Environment Guide`}</a>{`. Since Cordova takes advantage of web technologies, you won't need any specific additional tools beyond what you would use for web development.`}</p>
    <h3>{`Installing Cordova`}</h3>
    <p>{`The installation process for Cordova is a single command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`npm install -g cordova
// or
yarn global add cordova
`}</code></pre>
    <h2>{`Writing your application`}</h2>
    <p>{`Cordova is a great way to build a mobile app when you already have an existing web application (designed to be mobile-friendly).`}</p>
    <p>{`You can also use it for new projects - just build your web application as you normally would and add the Cordova project wrapper around it.`}</p>
    <blockquote>
      <p parentName="blockquote">{`To bring in Material Design components and Brightlayer UI themes to your application, follow our Getting Started Guides for `}<a parentName="p" {...{
          "href": "/development/frameworks-web/angular"
        }}>{`Angular`}</a>{` or `}<a parentName="p" {...{
          "href": "/development/frameworks-web/react"
        }}>{`React`}</a>{`.`}</p>
    </blockquote>
    <h3>{`Setting up a Cordova wrapper around your project`}</h3>
    <p>{`Start by creating a folder that will hold your entire project:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`mkdir myprojectfolder
cd myprojectfolder
`}</code></pre>
    <p>{`Then, create a new cordova wrapper project.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`cordova create cordova your.app.id AppName
`}</code></pre>
    <p>{`This will create a /cordova folder that will contain all of the necessary code to create the mobile app. You can see the `}<a parentName="p" {...{
        "href": "https://cordova.apache.org/docs/en/latest/reference/cordova-cli/index.html#cordova-create-command"
      }}>{`Cordova Documentation`}</a>{` to learn more about the parameters for the create command.`}</p>
    <p>{`From here, you will need to bring in the code for your web application. If you have an existing project, you can copy the root folder into the /myprojectfolder you created above (it should be at the same level as the /cordova folder). If you are starting a project from scratch, you can run one of the following commands in the /myprojectfolder directory, depending on your selected framework:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`create-react-app appname
ng new appname
`}</code></pre>
    <p>{`The resulting folder structure should look like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`└── /myprojectfolder
   |── /cordova
   └── /appname
`}</code></pre>
    <h2>{`Building your application`}</h2>
    <p>{`Cordova works by placing your application code (bundled and minified, as it would be for production) into the /cordova/www directory. We can automate this process to simplify the development process. Inside of your /appname directory, you should have a package.json file. In the "scripts" section, add an entry for the following:`}</p>
    <p>{`For Angular projects:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"build-cordova": "ng build --prod --base-href . --output-path ../cordova/www/"
`}</code></pre>
    <p>{`For React projects (Windows):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{` "build-cordova": "react-scripts build && (robocopy .\\\\build\\\\ ..\\\\cordova\\\\www\\\\ /e /purge ^& exit 0)"
`}</code></pre>
    <p>{`For React projects (Linux / Mac):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{` "build-cordova": "react-scripts build && rsync -aq ./build/ ../cordova/www/ --delete"
`}</code></pre>
    <p>{`If you are building a React app and your developers are using a mix of operating systems, you may want to include both versions of this script and give them different names (e.g., `}<inlineCode parentName="p">{`build-cordova-windows`}</inlineCode>{` and `}<inlineCode parentName="p">{`build-cordova-linux`}</inlineCode>{`).`}</p>
    <p>{`Once you have added this script, you can build your app and have the files moved to the /www directory with a single command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`cd appname
npm run build-cordova
// or
yarn build-cordova
`}</code></pre>
    <p>{`You can then add your desired platforms in your cordova project, and test them with the following commands:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`cd cordova
cordova platform add <platform>
cordova run <platform>
`}</code></pre>
    <p>{`For more information about the various Cordova commands, check out the `}<a parentName="p" {...{
        "href": "https://cordova.apache.org/docs/en/latest/guide/cli/"
      }}>{`Quick Start Guide`}</a>{` or the `}<a parentName="p" {...{
        "href": "https://cordova.apache.org/docs/en/latest/reference/cordova-cli/index.html"
      }}>{`API Reference`}</a>{`.`}</p>
    <p>{`When you are ready to create a final build, you'll use the build command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`cordova build <platform>
`}</code></pre>
    <h3>{`Using plugins`}</h3>
    <p>{`There are many different plugins available for use in Cordova applications that will allow you to access various native / hardware features of the device that your application is running on. To add a plugin to your app, navigate to the /cordova directory and install it:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`cd cordova
cordova plugin add <plugin>
`}</code></pre>
    <p>{`To use plugins in your application, you will need to add the cordova.js file as a script in your index.html file (or equivalent):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<script type="text/javascript" src="cordova.js"></script>
`}</code></pre>
    <p>{`This file is automatically created and added at the root level by the build process, so you do not need to modify the path in the src attribute.`}</p>
    <p>{`Then, make sure that the script is available before loading your application:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`For Angular projects, add the DOM manipulation code to a cordova.service.ts file:`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`let onDeviceReady = () => {
    console.log('cordova object is available');
};
document.addEventListener('deviceready', onDeviceReady, false);
`}</code></pre>
      </li>
      <li parentName="ul">
        <p parentName="li">{`For React projects, update your index.js file (or equivalent root file) to look like the following:`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`const startApp = () => {
    ReactDOM.render(<App />, document.getElementById('root'));
    registerServiceWorker();
};

if(!window.cordova) {
    document.addEventListener('deviceready', startApp, false);
}
else {
    startApp();
}
`}</code></pre>
      </li>
    </ul>
    <p>{`The plugins will then be available from the cordova object (window.cordova.`}<inlineCode parentName="p">{`<plugin_name>`}</inlineCode>{`.`}<inlineCode parentName="p">{`<function>`}</inlineCode>{`). Refer to the specific plugin documentation for proper usage.`}</p>
    <h2>{`License Information`}</h2>
    <p><a parentName="p" {...{
        "href": "https://github.com/apache/cordova-cli/blob/master/LICENSE"
      }}>{`Cordova`}</a>{` is available under the Apache 2.0 License.`}</p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;